import React, {
	useState,
	useRef,
	useEffect,
	KeyboardEvent,
	ClipboardEvent,
} from "react";
import {
	Field,
	Input as HeadlessInput,
	Label,
	Description,
} from "@headlessui/react";

export interface OTPInputProps {
	length?: number; // 2-8 digits
	onComplete?: (otp: string) => void;
	onChange?: (otp: string) => void;
	value?: string;
	label?: React.ReactNode;
	errorText?: React.ReactNode;
	helpText?: React.ReactNode;
	className?: string;
	autoFocus?: boolean;
	required?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
	length = 5,
	onComplete,
	onChange,
	value = "",
	label,
	required = false,
	errorText,
	helpText,
	className = "",
	autoFocus = false,
}) => {
	// Clamp length between 2 and 8
	const validLength = Math.min(Math.max(length, 2), 8);
	const hasError = !!errorText;

	const [otp, setOtp] = useState<string[]>(() => {
		const initial = value.split("").slice(0, validLength);
		return Array(validLength)
			.fill("")
			.map((_, i) => initial[i] || "");
	});

	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Sync with external value prop
	useEffect(() => {
		setOtp((currentOtp) => {
			if (value === currentOtp.join("")) {
				return currentOtp;
			}

			const digits = value.split("").slice(0, validLength);
			return Array(validLength)
				.fill("")
				.map((_, i) => digits[i] || "");
		});
	}, [value, validLength]);

	const handleChange = (index: number, inputValue: string) => {
		// Only allow single digit
		if (inputValue.length > 1) return;

		// Only allow numbers
		if (inputValue && !/^\d$/.test(inputValue)) return;

		const newOtp = [...otp];
		newOtp[index] = inputValue;
		setOtp(newOtp);

		const otpString = newOtp.join("");

		// Call onChange callback
		if (onChange) {
			onChange(otpString);
		}

		// Move to next input if value is entered
		if (inputValue && index < validLength - 1) {
			inputRefs.current[index + 1]?.focus();
		}

		// Call onComplete if all fields are filled
		if (newOtp.every((digit) => digit !== "") && onComplete) {
			onComplete(otpString);
		}
	};

	const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
		// Handle arrow keys for navigation
		if (e.key === "ArrowLeft" && index > 0) {
			e.preventDefault();
			inputRefs.current[index - 1]?.focus();
			return;
		}

		if (e.key === "ArrowRight" && index < validLength - 1) {
			e.preventDefault();
			inputRefs.current[index + 1]?.focus();
			return;
		}

		// Move to previous input on backspace if current input is empty
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			e.preventDefault();
			inputRefs.current[index - 1]?.focus();
		}

		// Delete key clears current and moves to previous
		if (e.key === "Delete" && otp[index] && index > 0) {
			e.preventDefault();
			const newOtp = [...otp];
			newOtp[index] = "";
			setOtp(newOtp);
			if (onChange) {
				onChange(newOtp.join(""));
			}
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text/plain").trim();

		// Only process if pasted data contains only digits
		if (!/^\d+$/.test(pastedData)) return;

		const digits = pastedData.slice(0, validLength).split("");
		const newOtp = [...otp];

		digits.forEach((digit, index) => {
			if (index < validLength) {
				newOtp[index] = digit;
			}
		});

		setOtp(newOtp);

		// Call onChange callback
		if (onChange) {
			onChange(newOtp.join(""));
		}

		// Focus on the next empty input or the last input
		const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
		if (nextEmptyIndex !== -1) {
			inputRefs.current[nextEmptyIndex]?.focus();
		} else {
			inputRefs.current[validLength - 1]?.focus();
			// Call onComplete if all fields are filled
			if (onComplete) {
				onComplete(newOtp.join(""));
			}
		}
	};

	const handleFocus = (index: number) => {
		// Select the input value on focus for easier editing
		inputRefs.current[index]?.select();
	};

	// Auto-focus first input if autoFocus is true
	useEffect(() => {
		if (autoFocus && inputRefs.current[0]) {
			inputRefs.current[0].focus();
		}
	}, [autoFocus]);

	return (
		<Field className={`w-full ${className}`}>
			{label && (
				<Label className="px-1 block mb-1 automint-input-error">
					{label}
					{required && <span className="text-red-500 ml-1">*</span>}
				</Label>
			)}
			<div className="flex justify-start items-center gap-3 w-full text-automint-text">
				{otp.map((digit, index) => (
					<HeadlessInput
						key={index}
						ref={(el) => {
							inputRefs.current[index] = el as HTMLInputElement | null;
						}}
						type="text"
						inputMode="numeric"
						pattern="\d*"
						maxLength={1}
						value={digit}
						autoComplete="off"
						autoCorrect="off"
						spellCheck={false}
						onChange={(e) => handleChange(index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
						onFocus={() => handleFocus(index)}
						invalid={hasError}
						className={`w-14 h-14  md:w-[82px] md:h-[70px] text-center border bg-white focus:outline-none focus:ring-2 focus:border-transparent text-automint-app-body ${
							hasError
								? "border-automint-error focus:ring-automint-error"
								: "border-gray-300 focus:ring-black"
						}`}
						style={{
							borderRadius: "var(--component-border-radius)",
						}}
					/>
				))}
			</div>
			<div className="min-h-[1.5rem] mt-2">
				{helpText && !errorText && (
					<Description className="w-full text-left text-automint-app-body text-second text-sm">
						{helpText}
					</Description>
				)}
				{errorText && (
					<Description className="w-full text-left text-automint-error inline-block text-xs font-normal">
						{errorText}
					</Description>
				)}
			</div>
		</Field>
	);
};
